import { useState, useEffect, useRef } from 'react';
import { applySeriesBackup } from '../utils/seriesTotals';
import { useAuth } from '../hooks/useAuth';
import {
  subscribeWishlist, exportFullBackup, parseBackup,
  restoreBDsBatch, restoreWishlistBatch,
} from '../utils/firestoreService';

const T = {
  orange:'var(--orange)', ink:'var(--ink)', bg:'var(--bg)', surface:'var(--surface)',
  border:'var(--border)', muted:'var(--muted)', subtle:'var(--subtle)', green:'#16A34A', red:'#DC2626',
};
const FONT    = "'Inter',-apple-system,BlinkMacSystemFont,sans-serif";
const DISPLAY = "'Bangers','Arial Black',cursive";

const fmtSize = b => b > 1048576 ? (b/1048576).toFixed(1)+' Mo' : Math.max(1,Math.round(b/1024))+' Ko';

export default function BackupModal({ bds, onClose, onToast }) {
  const { user } = useAuth();
  const [wishlist, setWishlist] = useState([]);
  const [parsed, setParsed]     = useState(null);   // { bds, wishlist, name }
  const [progress, setProgress] = useState(null);   // { done, total }
  const [error, setError]       = useState('');
  const fileRef = useRef(null);

  useEffect(() => {
    const unsub = subscribeWishlist(user.uid, setWishlist, () => {});
    return unsub;
  }, [user.uid]);

  const handleExport = () => {
    const r = exportFullBackup(bds, wishlist);
    onToast?.(`Sauvegarde téléchargée (${r.bds} albums, ${r.wishlist} souhaits · ${fmtSize(r.bytes)}) ✓`);
  };

  const handleFile = async e => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    setError('');
    try {
      const data = parseBackup(await f.text());
      setParsed({ ...data, name: f.name });
    } catch (err) { setParsed(null); setError(err.message); }
  };

  const handleRestore = async () => {
    if (!parsed) return;
    const total = parsed.bds.length + parsed.wishlist.length;
    setProgress({ done: 0, total });
    try {
      await restoreBDsBatch(user.uid, parsed.bds, d => setProgress({ done: d, total }));
      const base = parsed.bds.length;
      await restoreWishlistBatch(user.uid, parsed.wishlist, d => setProgress({ done: base + d, total }));
      applySeriesBackup(parsed.series);
      onToast?.(`${parsed.bds.length} albums et ${parsed.wishlist.length} souhaits restaurés ✓`);
      onClose();
    } catch (err) {
      setError('Erreur pendant la restauration : ' + (err.message || err));
      setProgress(null);
    }
  };

  const busy = !!progress;
  const btn = (primary, disabled) => ({
    width:'100%', padding:'13px', borderRadius:'12px', fontFamily:FONT, fontSize:'15px', fontWeight:700,
    cursor: disabled ? 'default' : 'pointer', border: primary ? 'none' : `1.5px solid ${T.border}`,
    background: disabled ? T.subtle : primary ? T.orange : T.surface,
    color: primary || disabled ? '#fff' : T.ink,
    boxShadow: primary && !disabled ? '0 4px 14px rgba(240,78,26,.3)' : 'none',
  });

  return (
    <div style={{position:'fixed',inset:0,background:'rgba(28,22,18,.6)',display:'flex',alignItems:'flex-end',justifyContent:'center',zIndex:1000,backdropFilter:'blur(6px)',WebkitBackdropFilter:'blur(6px)'}}
      onClick={e=>e.target===e.currentTarget&&!busy&&onClose()}>
      <div style={{background:T.bg,borderRadius:'22px 22px 0 0',border:`1px solid ${T.border}`,borderBottom:'none',width:'100%',maxWidth:'560px',maxHeight:'92vh',overflowY:'auto',fontFamily:FONT,boxShadow:'0 -12px 40px rgba(0,0,0,.18)'}}>
        <div style={{padding:'1rem 1.25rem',borderBottom:`1px solid ${T.border}`,display:'flex',alignItems:'center',justifyContent:'space-between'}}>
          <div>
            <div style={{fontFamily:DISPLAY,fontSize:'1.35rem',letterSpacing:'.05em',color:T.orange}}>💾 Sauvegarde</div>
            <div style={{fontSize:'12px',color:T.muted,marginTop:'1px'}}>Export et restauration complets</div>
          </div>
          <button onClick={onClose} disabled={busy} style={{width:'32px',height:'32px',borderRadius:'50%',border:`1px solid ${T.border}`,background:T.surface,cursor:'pointer',fontSize:'16px',color:T.muted}}>×</button>
        </div>

        <div style={{padding:'1.25rem',display:'flex',flexDirection:'column',gap:'18px'}}>
          {/* Export */}
          <div>
            <div style={{fontSize:'13px',fontWeight:700,color:T.ink,marginBottom:'4px'}}>Télécharger une sauvegarde</div>
            <div style={{fontSize:'12px',color:T.muted,lineHeight:1.5,marginBottom:'10px'}}>
              Un fichier JSON avec tous tes albums (photos comprises) et ta liste de souhaits :
              {' '}<strong>{bds.length}</strong> album{bds.length>1?'s':''}, <strong>{wishlist.length}</strong> souhait{wishlist.length>1?'s':''}.
            </div>
            <button onClick={handleExport} disabled={busy} style={btn(true, busy)}>⬇ Télécharger la sauvegarde</button>
          </div>

          <div style={{borderTop:`1px solid ${T.border}`}}/>

          {/* Restauration */}
          <div>
            <div style={{fontSize:'13px',fontWeight:700,color:T.ink,marginBottom:'4px'}}>Restaurer depuis un fichier</div>
            <div style={{fontSize:'12px',color:T.muted,lineHeight:1.5,marginBottom:'10px'}}>
              Les albums gardent leur identifiant d'origine : relancer une restauration ne crée pas de doublons, et un album existant avec le même identifiant est <strong>remplacé</strong> par la version du fichier. Rien n'est supprimé.
            </div>
            <input ref={fileRef} type="file" accept="application/json,.json" onChange={handleFile} style={{display:'none'}}/>
            {!parsed && <button onClick={()=>fileRef.current?.click()} disabled={busy} style={btn(false, busy)}>📂 Choisir un fichier de sauvegarde</button>}

            {parsed && (
              <div style={{border:`1.5px solid ${T.border}`,borderRadius:'12px',padding:'12px',background:T.surface}}>
                <div style={{fontSize:'13px',fontWeight:600,color:T.ink,marginBottom:'2px',wordBreak:'break-all'}}>{parsed.name}</div>
                <div style={{fontSize:'12px',color:T.muted,marginBottom:'12px'}}>
                  {parsed.bds.length} album{parsed.bds.length>1?'s':''} · {parsed.wishlist.length} souhait{parsed.wishlist.length>1?'s':''}
                </div>
                {busy ? (
                  <>
                    <div style={{height:'8px',background:T.border,borderRadius:'99px',overflow:'hidden'}}>
                      <div style={{height:'100%',width:`${Math.round(progress.done/Math.max(progress.total,1)*100)}%`,background:T.orange,transition:'width .3s'}}/>
                    </div>
                    <div style={{fontSize:'12px',color:T.muted,marginTop:'6px',textAlign:'center'}}>Restauration… {progress.done}/{progress.total}</div>
                  </>
                ) : (
                  <div style={{display:'flex',gap:'8px'}}>
                    <button onClick={()=>setParsed(null)} style={{...btn(false,false),flex:1,padding:'11px',fontSize:'14px'}}>Annuler</button>
                    <button onClick={handleRestore} style={{...btn(true,false),flex:2,padding:'11px',fontSize:'14px'}}>Restaurer</button>
                  </div>
                )}
              </div>
            )}
          </div>

          {error && <div style={{background:'#FEF2F2',border:'1px solid #FECACA',borderRadius:'9px',padding:'10px 12px',fontSize:'13px',color:T.red,fontWeight:500}}>{error}</div>}
        </div>
      </div>
    </div>
  );
}
